import { api } from '@/lib/api';
import type { GroupInvoiceAdjustment, LocalizedText } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// List view: simplified registration for table display
export interface RegistrationListItem {
  id: string;
  groupId: string;
  email: string;
  language: 'nl' | 'fr' | 'en';
  currentData: Record<string, unknown>;
  manualOverrides: Record<string, unknown> | null;
  invoiceAdjustments: Array<
    | {
        type: 'set_line_amount';
        scope: 'non_split' | 'person1' | 'person2';
        targetArticleNumber: string;
        amount: string;
      }
    | {
        type: 'add_line';
        scope: 'non_split' | 'person1' | 'person2';
        articleNumber: string;
        description: string;
        amount: string;
      }
  > | null;
  isSplitBilling: boolean;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  firstSubmittedAt: string;
  lastSubmittedAt: string;
  payments: {
    id: string;
    personNumber: number;
    status: 'created' | 'pending' | 'open' | 'paid' | 'failed' | 'expired' | 'canceled';
    createdAt: string;
  }[];
  invoices: {
    id: string;
    paymentId: string | null;
    type: 'DEPOSIT' | 'BALANCE';
    exactStatus: 'DRAFT' | 'QUEUED' | 'SYNCED' | 'FAILED' | 'MATCHED' | 'MANUALLY_RESOLVED';
    errorMessage?: string | null;
  }[];
}

// Submission type
export interface Submission {
  id: string;
  registrationId: string;
  groupId: string;
  externalSubmissionId: string;
  rawPayload: Record<string, unknown>;
  normalizedData: Record<string, unknown> | null;
  submissionType: 'initial' | 'amendment';
  createdAt: string;
  updatedAt: string;
}

// Invoice type
export interface Invoice {
  id: string;
  registrationId: string;
  paymentId: string | null;
  triggeringSubmissionId: string | null;
  type: 'DEPOSIT' | 'BALANCE';
  recipientName: string;
  exactInvoiceId: string | null;
  exactInvoiceNumber: string | null;
  exactStatus: 'DRAFT' | 'QUEUED' | 'SYNCED' | 'FAILED' | 'MATCHED' | 'MANUALLY_RESOLVED';
  errorMessage?: string | null;
  amountIncl: string | null;
  personNumber: number | null;
  invoiceLines: Array<{ description: string; quantity: number; unitPrice: number; vatCode: string }> | null;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  registrationId: string;
  personNumber: number;
  mollieMode?: 'test' | 'live';
  molliePaymentId: string | null;
  amount: string;
  currency: string;
  status: 'created' | 'pending' | 'open' | 'paid' | 'failed' | 'expired' | 'canceled';
  checkoutUrl: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegistrationInvoicePreviewLine {
  label: string;
  amount: string;
  articleNumber?: string;
  itemCode?: string;
}

export interface RegistrationInvoicePreviewBreakdown {
  lines: RegistrationInvoicePreviewLine[];
  total: string;
}

export interface RegistrationInvoicePreview {
  isSplitBilling: boolean;
  participantCount: number;
  nonSplit: RegistrationInvoicePreviewBreakdown | null;
  person1: RegistrationInvoicePreviewBreakdown | null;
  person2: RegistrationInvoicePreviewBreakdown | null;
  grandTotal: string;
}

// Group type (nested in registration detail)
export interface RegistrationGroup {
  id: string;
  projectId: string;
  name: LocalizedText;
  packagePrice: string;
  singleRoomSupplement?: string | null;
  invoiceAdjustments: GroupInvoiceAdjustment[] | null;
  isActive: boolean;
}

// Detail view: full registration with relations
export interface RegistrationDetail extends RegistrationListItem {
  createdAt: string;
  updatedAt: string;
  group: RegistrationGroup;
  submissions: Submission[];
  invoices: Invoice[];
  payments: Payment[];
}

export interface MergeRegistrationResult {
  success: true;
  targetRegistration: RegistrationListItem;
  sourceRegistrationId: string;
  movedSubmissionCount: number;
  movedPaymentCount: number;
  movedInvoiceCount: number;
  currentDataAddedKeys: string[];
  currentDataSkippedKeys: string[];
  manualOverridesAddedKeys: string[];
  manualOverridesSkippedKeys: string[];
}

/**
 * Hook to fetch all registrations for a group
 */
export function useGroupRegistrations(groupId: string) {
  return useQuery({
    queryKey: ['groups', groupId, 'registrations'],
    queryFn: () => api.get<RegistrationListItem[]>(`/groups/${groupId}/registrations`),
    enabled: !!groupId,
  });
}

/**
 * Hook to fetch single registration with full details
 */
export function useRegistration(id: string) {
  return useQuery({
    queryKey: ['registrations', id],
    queryFn: () => api.get<RegistrationDetail>(`/registrations/${id}`),
    enabled: !!id,
  });
}

export function useRegistrationInvoicePreview(
  id: string,
  payload?: {
    manualOverrides?: Record<string, unknown>;
    isSplitBilling?: boolean;
    invoiceAdjustments?: RegistrationListItem['invoiceAdjustments'];
  }
) {
  return useQuery({
    queryKey: ['registrations', id, 'invoice-preview', payload],
    queryFn: () => api.post<RegistrationInvoicePreview>(`/registrations/${id}/invoice-preview`, payload || {}),
    enabled: !!id,
  });
}

/**
 * Hook to create a balance invoice in Exact Online for a registration
 */
export function useCreateBalanceInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.post<{ invoices: { id: string; exactInvoiceId: string; invoiceNumber: string; amountIncl: string }[] }>(
        `/registrations/${id}/create-balance-invoice`,
        {}
      ),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', id] });
      const numbers = data.invoices.map(inv => inv.invoiceNumber).join(', ');
      toast.success(`Saldo-factuur aangemaakt: ${numbers}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to retry syncing a FAILED or QUEUED deposit invoice to Exact Online
 */
export function useRetryDepositInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ registrationId, invoiceId }: { registrationId: string; invoiceId: string }) =>
      api.post<{ exactInvoiceNumber: string }>(
        `/registrations/${registrationId}/retry-deposit-invoice/${invoiceId}`,
        {}
      ),
    onSuccess: (data, { registrationId }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', registrationId] });
      toast.success(`Voorschot-factuur aangemaakt: ${data.exactInvoiceNumber}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to stop retries after an invoice was resolved outside the application.
 */
export function useMarkInvoiceManuallyResolved() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ registrationId, invoiceId }: { registrationId: string; invoiceId: string }) =>
      api.post<{ success: true }>(`/registrations/${registrationId}/mark-invoice-manually-resolved/${invoiceId}`, {}),
    onSuccess: (_, { registrationId }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', registrationId] });
      toast.success('Factuur gemarkeerd als handmatig');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to regenerate all DEPOSIT invoices for a registration.
 * Replaces FAILED/QUEUED ones and creates missing ones, using the current
 * merged billing data (incl. manual overrides / fixed VAT number).
 * SYNCED invoices are left untouched.
 */
export function useRegenerateDepositInvoices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.post<{ created: number; skipped: number; invoiceNumbers: string[] }>(
        `/registrations/${id}/regenerate-deposit-invoices`,
        {}
      ),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', id] });
      if (data.created === 0) {
        toast.success('Alle voorschot-facturen waren al aangemaakt (niets te doen)');
      } else {
        const numbers = data.invoiceNumbers.join(', ');
        toast.success(`${data.created} voorschot-factuur/facturen aangemaakt: ${numbers}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to update registration manual overrides
 */
export function useUpdateRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      manualOverrides,
      isSplitBilling,
      invoiceAdjustments,
    }: {
      id: string;
      manualOverrides?: Record<string, unknown>;
      isSplitBilling?: boolean;
      invoiceAdjustments?: RegistrationListItem['invoiceAdjustments'];
    }) =>
      api.patch<RegistrationDetail>(`/registrations/${id}`, { manualOverrides, isSplitBilling, invoiceAdjustments }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Registratie bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken registratie: ${error.message}`);
    },
  });
}

export function useChangeParticipantCount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      participantCount,
      removedPerson,
      person2,
    }: {
      id: string;
      participantCount: 1 | 2;
      removedPerson?: 1 | 2;
      person2?: { firstName: string; lastName: string; email?: string };
    }) =>
      api.post<RegistrationDetail>(`/registrations/${id}/change-participant-count`, {
        participantCount,
        removedPerson,
        person2,
      }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Aantal deelnemers bijgewerkt');
    },
    onError: (error: Error) => toast.error(`Fout bij wijzigen aantal deelnemers: ${error.message}`),
  });
}

export function useDeleteRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.delete(`/registrations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['registrations'] });
    },
  });
}

export function useMergeRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      targetRegistrationId,
      sourceRegistrationId,
      reason,
    }: {
      targetRegistrationId: string;
      sourceRegistrationId: string;
      reason?: string;
    }) =>
      api.post<MergeRegistrationResult>(`/registrations/${targetRegistrationId}/merge`, {
        sourceRegistrationId,
        reason,
      }),
    onSuccess: (_, { targetRegistrationId }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', targetRegistrationId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Registraties samengevoegd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij samenvoegen: ${error.message}`);
    },
  });
}

export function useCancelRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<RegistrationDetail>(`/registrations/${id}/cancel`, { reason }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Registratie geannuleerd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij annuleren: ${error.message}`);
    },
  });
}

export function useReactivateRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.post<RegistrationDetail>(`/registrations/${id}/reactivate`, {}),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['registrations', id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Registratie geheractiveerd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij heractiveren: ${error.message}`);
    },
  });
}
